import type { Paths } from "./dotNotationTypes";

export interface Result<T> {
  readonly success: boolean;
  readonly result: T | undefined;
}

export function getValueAsString<T>(obj: T, pathString: Paths<T, string>): Result<string> {
  const { success, result: stringValue } = getValue<T, string>(obj, pathString);
  if (!success) {
    return { success: false, result: undefined };
  }

  if (typeof stringValue !== "string") {
    console.error("Expected string but got", stringValue, "from", obj, "path", pathString);
    return { success: false, result: undefined };
  }

  return { success: true, result: stringValue };
}

export function getValueAsStringOrUndefined<T>(obj: T, pathString: Paths<T, string | undefined>): Result<string> {
  const { success, result: stringValue } = getValue<T, string | undefined>(obj, pathString);
  if (!success) {
    return { success: false, result: undefined };
  }

  if (typeof stringValue !== "string" && typeof stringValue !== "undefined") {
    console.error("Expected string or undefined but got", stringValue, "from", obj, "path", pathString);
    return { success: false, result: undefined };
  }

  return { success: true, result: stringValue };
}

export function getValueAsNumber<T>(obj: T, pathString: Paths<T, number>): Result<number> {
  const { success, result: numberValue } = getValue<T, number>(obj, pathString);
  if (!success) {
    return { success: false, result: undefined };
  }

  if (typeof numberValue !== "number") {
    console.error("Expected number but got", numberValue, "from", obj, "path", pathString);
    return { success: false, result: undefined };
  }

  return { success: true, result: numberValue };
}

export function getValueAsNumberOrUndefined<T>(obj: T, pathString: Paths<T, number>): Result<number> {
  const { success, result: numberValue } = getValue<T, number>(obj, pathString);
  if (!success) {
    return { success: false, result: undefined };
  }

  if (typeof numberValue !== "number" && typeof numberValue !== "undefined") {
    console.error("Expected number or undefined but got", numberValue, "from", obj, "path", pathString);
    return { success: false, result: undefined };
  }

  return { success: true, result: numberValue };
}

export function getValueAsBooleanOrUndefined<T>(obj: T, pathString: Paths<T, boolean>): Result<boolean> {
  const { success, result: booleanValue } = getValue<T, boolean>(obj, pathString);
  if (!success) {
    return { success: false, result: undefined };
  }

  if (typeof booleanValue !== "boolean" && typeof booleanValue !== "undefined") {
    console.error("Expected boolean or undefined but got", booleanValue, "from", obj, "path", pathString);
    return { success: false, result: undefined };
  }

  return { success: true, result: booleanValue };
}

export function getValue<T, U>(obj: T, pathString: Paths<T, U>): Result<U> {
  if (pathString === "") {
    return { success: true, result: obj as unknown as U };
  }

  let value = { ...obj } as U | T;
  const paths = (pathString as string).split(".");
  for (const index of Object.keys(paths)) {
    const path = paths[+index];

    let currentValue: U;
    // check that every value is a valid path and an object except the last one
    if (+index !== paths.length - 1) {
      if (!value?.hasOwnProperty(path)) {
        console.error("Expected object", value, "to have field", path);
        return { success: false, result: undefined };
      }

      currentValue = (value as Record<string, any>)[path] as U;

      if (typeof currentValue !== "object") {
        console.error("Expected object but got", currentValue, "from", value, "path", pathString);
        return { success: false, result: undefined };
      }
    } else {
      // sometimes the final path is not on the object and that's okay, so we don't check the path exists
      currentValue = (value as Record<string, any>)[path] as U;
    }

    value = currentValue;
    // all but last value should be an object
  }

  return { success: true, result: value as U };
}

export function setValue<T, U, V = any>(obj: U, pathString: Paths<T, V>, value: V | undefined): Result<U> {
  if (pathString === "") {
    return { success: true, result: value as unknown as U & V }; // U & V because the value type being set is the same as the object
  }

  const paths = (pathString as string).split(".");
  let ref = obj as Record<string, any>;
  for (let i = 0; i < paths.length - 1; i++) {
    if (!ref?.hasOwnProperty(paths[i])) {
      console.error("Expected object", ref, "to have field", paths[i]);
      return { success: false, result: undefined };
    }

    // every value here should be an object
    ref = ref[paths[i]] as Record<string, any>;
  }

  if (!ref?.hasOwnProperty(paths[paths.length - 1])) {
    console.error("Expected object", ref, "to have field", paths[paths.length - 1]);
    return { success: false, result: undefined };
  }

  ref[paths[paths.length - 1]] = value;
  return { success: true, result: obj as U };
}
